// Demo data for the analytics pages. Entirely made up: no real store, supplier or sale.
import type { NetworkModel, PriceCheckItem, SalesPoint, SupplierModel } from '../lib/analytics';

const isoDay = (offset: number) => {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Deterministic, so the demo looks the same on every load.
const wave = (i: number, base: number, swing: number) =>
  Math.max(0, Math.round(base + swing * Math.sin(i / 2.3) + (swing / 2) * Math.cos(i * 1.7)));

const series = (days: number, shift: number, base: number): SalesPoint[] =>
  Array.from({ length: days }, (_, i) => {
    const orders = wave(i + shift, base, base * 0.45);
    return { date: isoDay(days - 1 - i + shift), orders, units: orders + (i % 4 === 0 ? 1 : 0), revenue: orders * (128 + (i % 5) * 6) };
  });

const sum = (points: SalesPoint[], key: 'revenue' | 'orders' | 'units') => points.reduce((t, p) => t + p[key], 0);

const MODELS: Array<[string, string, string]> = [
  ['DM-1001', 'Runner 90 Triple White', 'Northline'],
  ['DM-1002', 'Gel Street Oyster Grey', 'Kasai'],
  ['DM-1003', 'Clog Classic Taupe', 'Bergen'],
  ['DM-1004', 'Runner 90 Triple Black', 'Northline'],
  ['DM-1005', 'P-Series Metallic Silver', 'Northline'],
  ['DM-1006', 'Gel Street Concrete Rose', 'Kasai'],
  ['DM-1007', 'Terrace OG Leopard', 'Trefoil'],
  ['DM-1008', 'Court Low Panda', 'Northline'],
  ['DM-1009', 'Terrace Jane Black Gum', 'Trefoil'],
  ['DM-1010', 'Campus Grey Gum', 'Trefoil'],
  ['DM-1011', 'Max Plus Sunset', 'Northline'],
  ['DM-1012', 'Trail 2002 Protection Pack', 'Balance Co'],
  ['DM-1013', 'Gel Kayo Cream Plum', 'Kasai'],
  ['DM-1014', 'High Retro Fir Green', 'Northline'],
];

const STATUSES: NetworkModel['status'][] = [
  'selling', 'selling', 'available', 'listed', 'selling', 'other_supplier', 'available', 'listed',
  'removed', 'listed_no_stock', 'not_available', 'selling', 'available', 'listed',
];
const TRENDS: NetworkModel['trend'][] = ['up', 'up', 'steady', 'new', 'up', 'down', 'up', 'steady', 'new', 'down'];

const base = (i: number) => ({
  rank: i + 1,
  sku: MODELS[i][0],
  title: MODELS[i][1],
  brand: MODELS[i][2],
  photo_url: null,
  demand: (i < 5 ? 'very_high' : i < 10 ? 'high' : 'steady') as NetworkModel['demand'],
  trend: TRENDS[i % TRENDS.length],
  top_sizes: ['42', '43', '44.5', '41'].slice(0, 2 + (i % 3)),
});

const networkModels: NetworkModel[] = MODELS.map((_, i) => ({
  ...base(i),
  status: STATUSES[i],
  my_units: STATUSES[i] === 'selling' ? 9 - (i % 5) : 0,
  from_price: STATUSES[i] === 'available' ? 62 + i * 3 : null,
  addable_from: STATUSES[i] === 'available' ? ['Demo Supply Co'] : [],
  addable_in_stock: STATUSES[i] === 'available' && i !== 12,
  other_suppliers: STATUSES[i] === 'other_supplier' ? 2 : 0,
}));

const prices: PriceCheckItem[] = [
  { sku: 'DM-1004', rank: 4, verdict: 'above', gap_band: '15-30', my_price: 189.99, my_price_max: 199.99, currency: 'EUR' },
  { sku: 'DM-1014', rank: 14, verdict: 'above', gap_band: '5-15', my_price: 159.99, my_price_max: 159.99, currency: 'EUR' },
  { sku: 'DM-1001', rank: 1, verdict: 'in_line', gap_band: null, my_price: 149.99, my_price_max: 149.99, currency: 'EUR' },
];

export const analyticsRpcHandlers: Record<string, (params: any) => any> = {
  get_merchant_sales_overview: (params: any) => {
    const days = params.p_days ?? 30;
    const cur = series(days, 0, 14);
    const prev = series(days, days, 11);
    const units = sum(cur, 'units');
    return {
      currency: 'EUR',
      timezone: 'UTC',
      days,
      from: cur[0].date,
      to: cur[cur.length - 1].date,
      series: cur,
      previous_series: prev,
      totals: {
        revenue: sum(cur, 'revenue'),
        orders: sum(cur, 'orders'),
        units,
        supplier_units: Math.round(units * 0.64),
        own_units: units - Math.round(units * 0.64),
        other_currency_orders: 0,
      },
      previous_totals: { revenue: sum(prev, 'revenue'), orders: sum(prev, 'orders'), units: sum(prev, 'units') },
    };
  },
  get_merchant_network_insights: (params: any) => ({ days: params.p_days ?? 30, models: networkModels }),
  analytics_add_model_to_store: (params: any) => {
    const m = networkModels.find((x) => x.sku === params.p_sku);
    if (m) m.status = m.addable_in_stock ? 'listed' : 'listed_no_stock';
    return { sku: params.p_sku, added_from: ['Demo Supply Co'], already_selected: false };
  },
  get_provider_demand: (params: any) => ({
    days: params.p_days ?? 30,
    summary: { won: 84, lost_on_price: 17, lost_no_stock: 23, avg_gap: 7.4 },
    results: MODELS.slice(0, 6).map((m, i) => ({
      sku: m[0],
      title: m[1],
      won: 12 - i,
      lost_on_price: i % 3,
      lost_no_stock: (i * 2) % 5,
      missed: (i % 3) + ((i * 2) % 5),
      avg_gap: i % 3 ? 4.5 + i : null,
    })),
    models: MODELS.map((_, i): SupplierModel => ({
      ...base(i),
      status: i % 3 === 0 ? 'in_stock' : i % 3 === 1 ? 'out_of_stock' : 'not_carried',
    })),
  }),
};

export const analyticsEdgeHandlers: Record<string, (body: any) => any> = {
  'merchant-analytics': () => ({ items: prices }),
};
